import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");
const STATE = {
  sent: { dot: "bg-emerald-500", text: "text-emerald-800", label: "Sent" },
  due: { dot: "bg-amber-500 animate-pulse", text: "text-amber-800", label: "Due at next 10:00 run" },
  skipped: { dot: "bg-slate-300", text: "text-slate-500", label: "Skipped" },
  pending: { dot: "bg-slate-200", text: "text-slate-500", label: "Waiting" },
};

/** ✉️ Order page: the 7-step customer journey with sent / opened state and Send-now buttons (BO / SA). */
export function JourneyTimeline({ orderId, user }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const isStaff = ["back_office", "super_admin"].includes(user?.role);
  const q = useQuery({ queryKey: ["order-journey", orderId], queryFn: async () => (await api.get(`/orders/${orderId}/journey`)).data, enabled: !!orderId });
  const send = useMutation({
    mutationFn: async (key) => (await api.post(`/orders/${orderId}/journey/send/${key}`)).data,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["order-journey", orderId] }); qc.invalidateQueries({ queryKey: ["order", orderId] }); toast.success(r.suppressed ? "Logged (preview environment — not delivered)" : `Sent to ${r.customer || "the customer"}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send"),
  });
  const j = q.data;
  if (!j) return null;
  const sentCount = j.steps.filter((s) => s.state === "sent").length;
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="journey-timeline">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-4 py-2.5 text-left" data-testid="journey-timeline-toggle">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-semibold">✉️ Customer journey e-mails</span>
          <span className="text-xs text-slate-500">{sentCount}/{j.steps.length} sent{j.cycle > 0 ? ` · set #${j.cycle + 1} (after QC send-back)` : ""}</span>
          <div className="hidden sm:flex items-center gap-1">
            {j.steps.map((s) => <span key={s.key} className={`inline-block h-2.5 w-2.5 rounded-full ${STATE[s.state]?.dot || STATE.pending.dot}`} title={`${s.label}: ${STATE[s.state]?.label}`} />)}
          </div>
          {!j.enabled && <span className="text-[10px] uppercase tracking-widest font-mono text-amber-700">journey OFF</span>}
          {j.skip_reason && j.enabled && <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500" data-testid="journey-skip-reason">{j.skip_reason}</span>}
        </div>
        <span className="text-slate-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2">
          <ol className="space-y-1.5">
            <li className="flex items-center gap-3 text-sm"><span className={`h-2.5 w-2.5 rounded-full ${j.signed_at ? "bg-emerald-500" : "bg-slate-200"}`} /><span className="w-40 shrink-0 font-semibold">1 · Signed off</span><span className="text-xs text-slate-500">{j.signed_at ? fmt(j.signed_at) : "waiting for the factory signature"}</span></li>
            {j.steps.map((s) => {
              const st = STATE[s.state] || STATE.pending;
              return (
                <li key={s.key} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`journey-step-${s.key}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${st.dot}`} />
                  <span className="w-40 shrink-0 font-semibold">{s.step} · {s.label}</span>
                  <span className={`text-xs ${st.text}`} data-testid={`journey-step-${s.key}-state`}>
                    {s.state === "sent" && <>Sent {fmt(s.at)}{s.suppressed ? " (not delivered — preview)" : ""}{s.opened_at ? ` · opened ${fmt(s.opened_at)}` : " · not opened yet"}{s.clicked_at ? " · clicked" : ""}</>}
                    {s.state === "skipped" && <>Skipped — {s.reason}</>}
                    {s.state === "due" && <>Due at the next 10:00 run ({fmt(j.next_run)})</>}
                    {s.state === "pending" && <>{s.expected_at ? `Expected ${fmt(s.expected_at)}` : "Waiting for the event"}{!s.enabled ? " · template OFF" : ""}</>}
                  </span>
                  <span className="text-[10px] text-slate-400">{s.name}</span>
                  {isStaff && s.state !== "sent" && j.manual_allowed && s.can_send && (
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={send.isPending} onClick={() => { if (window.confirm(`Send "${s.name}" to the customer now?`)) send.mutate(s.key); }} data-testid={`journey-send-${s.key}`}>Send now</Button>
                  )}
                  {isStaff && s.state !== "sent" && !s.can_send && <span className="text-[10px] text-slate-400" data-testid={`journey-nophoto-${s.key}`}>needs the packing photo on Drive</span>}
                </li>
              );
            })}
            <li className="flex items-center gap-3 text-sm"><span className="h-2.5 w-2.5 rounded-full bg-slate-200" /><span className="w-40 shrink-0 font-semibold">7 · Tracking</span><span className="text-xs text-slate-500">the shipping notification with the tracking number (existing e-mail)</span></li>
          </ol>
          {(j.qc_events || []).length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs" data-testid="journey-qc-events">
              {j.qc_events.map((e, i) => (
                <div key={i}>🛑 {fmt(e.at)} · QC send-back ({e.po_number}) — customer note {e.apology?.ok ? `sent to ${e.apology.to}` : `not sent (${e.apology?.error || "—"})`} · admins {e.alert?.ok ? `alerted (${(e.alert.to || []).length})` : `not alerted (${e.alert?.error || "—"})`}</div>
              ))}
            </div>
          )}
          {isStaff && j.manual_allowed && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <Link to="/settings#journey-emails" className="underline" data-testid="journey-settings-link">Journey settings & templates</Link>
              {j.enabled ? <span>· runs every day at 10:00 Toronto</span> : <span>· journey e-mails are OFF — switch on in Settings</span>}
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] ml-auto" disabled={send.isPending} onClick={() => { if (window.confirm("Send the QC “one more week” apology to the customer now?")) send.mutate("journey_qc_delay"); }} data-testid="journey-send-qc-delay">Send “one more week” note</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
